/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import _ from 'lodash';

import {
  Dimmer,
  DimmerDimmable,
  Header,
  Icon,
  Loader,
  Segment,
  Table,
} from '@mairistem/composants';

import { _date, Section } from '@mairistem/administres-utils';

import type { useActeIndividus } from '@/hooks';
import type { Acte, ActeIndividu } from '@/types';
import { trimComma } from '@/utils';

const TYPE_INDIVIDU_LABELS: Record<string, string> = {
  enfant: 'Enfant',
  père: 'Père',
  mère: 'Mère',
  époux: 'Époux',
  épouse: 'Épouse',
  'défunt(e)': 'Défunt(e)',
  déclarant: 'Déclarant',
  notaire: 'Notaire',
  médecin: 'Médecin',
};

interface Props {
  selectedActe: Acte | null;
  acteIndividus: ReturnType<typeof useActeIndividus>;
  selectedIndividu: ActeIndividu | null;
  onIndividuSelected: (individu: ActeIndividu) => void;
}

const PersonneBlock = ({
  selectedActe,
  acteIndividus,
  selectedIndividu,
  onIndividuSelected,
}: Props) => (
  <Section
    title={`PERSONNES SUR L'ACTE ${selectedActe ? `— Acte n°${selectedActe.numeroActe} (${selectedActe.anneeActe})` : ''}`}
    style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
  >
    {!selectedActe ? (
      <Segment placeholder>
        <Header icon>
          <Icon name="hand point left outline" />
          Sélectionnez un acte
        </Header>
      </Segment>
    ) : _.isEmpty(acteIndividus.data) && !acteIndividus.loading ? (
      <Segment placeholder>
        <Header icon>
          <Icon name="user outline" />
          Aucune personne trouvée sur cet acte
        </Header>
      </Segment>
    ) : (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'scroll' }}>
        <Table celled selectable style={{ tableLayout: 'fixed' }}>
          <Table.Header style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <Table.Row>
              <Table.HeaderCell content="Nom Prénom" style={{ width: '50%' }} />
              <Table.HeaderCell
                content="Date de naissance"
                style={{ width: '25%' }}
              />
              <Table.HeaderCell content="Rôle" style={{ width: '25%' }} />
            </Table.Row>
          </Table.Header>
          <DimmerDimmable
            as={Table.Body}
            dimmed={acteIndividus.loading}
            {...(acteIndividus.loading && { style: { height: 80 } })}
          >
            <Dimmer active={acteIndividus.loading} inverted>
              <Loader indeterminate />
            </Dimmer>
            {_.map(acteIndividus.data, (individu) => {
              const isDisabled = !individu.nomFamille && !individu.prenom;
              return (
                <Table.Row
                  key={individu.identifiant}
                  selectable={!isDisabled}
                  active={!isDisabled && selectedIndividu === individu}
                  disabled={isDisabled}
                  onClick={
                    isDisabled ? undefined : () => onIndividuSelected(individu)
                  }
                  style={{ cursor: isDisabled ? 'default' : 'pointer' }}
                >
                  <Table.Cell
                    content={[
                      trimComma(individu.nomFamille),
                      trimComma(individu.prenom),
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                  <Table.Cell
                    content={_date.short(individu.naissanceDate ?? '')}
                  />
                  <Table.Cell
                    content={
                      TYPE_INDIVIDU_LABELS[
                        individu.typeIndividuActe?.toLowerCase() ?? ''
                      ] ||
                      individu.typeIndividuActe ||
                      '—'
                    }
                  />
                </Table.Row>
              );
            })}
          </DimmerDimmable>
        </Table>
      </div>
    )}
  </Section>
);

export default PersonneBlock;
